import { useState, useEffect } from 'react';
import { Building } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export const RetirementCalculator = () => {
  const [currentAge, setCurrentAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(65);
  const [currentSavings, setCurrentSavings] = useState(200000);
  const [monthlyContribution, setMonthlyContribution] = useState(5000);
  const [monthlyRetirementNeeds, setMonthlyRetirementNeeds] = useState(35000);
  
  const [totalAtRetirement, setTotalAtRetirement] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const yearsToRetirement = retirementAge - currentAge;
    const expectedReturn = 6;
    const monthlyRate = expectedReturn / 100 / 12;
    
    let balance = currentSavings;
    const data = [];

    for (let year = 0; year <= yearsToRetirement; year++) {
      if (year === 0) {
        data.push({ age: currentAge, portfolio: currentSavings });
      } else {
        for (let month = 0; month < 12; month++) {
          balance = balance * (1 + monthlyRate) + monthlyContribution;
        }
        data.push({ age: currentAge + year, portfolio: Math.round(balance) });
      }
    }

    setTotalAtRetirement(Math.round(balance));
    setMonthlyIncome(Math.round((balance * 0.04) / 12));
    setChartData(data);
  }, [currentAge, retirementAge, currentSavings, monthlyContribution]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">Věk: {label} let</p>
          <p className="text-green font-bold">{payload[0]?.value?.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const isOnTrack = monthlyIncome >= monthlyRetirementNeeds;

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">V {retirementAge} letech budete mít</p>
        <p className="text-4xl font-bold text-green">{totalAtRetirement.toLocaleString('cs-CZ')} Kč</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Měsíční renta (4%)</p>
          <p className={`font-bold text-lg ${isOnTrack ? 'text-green' : 'text-gold'}`}>
            {monthlyIncome.toLocaleString('cs-CZ')} Kč
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Potřebujete</p>
          <p className="text-white font-bold text-lg">{monthlyRetirementNeeds.toLocaleString('cs-CZ')} Kč</p>
        </div>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorPortfolio" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22C55E" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="age" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="portfolio" 
              stroke="#22C55E" 
              fill="url(#colorPortfolio)" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className={`p-4 rounded-xl ${isOnTrack ? 'bg-green/10' : 'bg-gold/10'}`}>
        <p className={`text-sm ${isOnTrack ? 'text-green' : 'text-gold'}`}>
          {isOnTrack 
            ? `✓ Vaše renta pokryje potřeby`
            : `⚠ Chybí ${(monthlyRetirementNeeds - monthlyIncome).toLocaleString('cs-CZ')} Kč měsíčně`}
        </p>
      </div>
    </div>
  );

  return (
    <CalculatorCard
      id="duchod"
      title="Důchodová kalkulačka"
      subtitle="Plánujte svůj odchod do důchodu"
      icon={Building}
      tip="Na státní důchod se nespoléhejte – průměr je ~18 000 Kč. Pro důstojný důchod potřebujete vlastní úspory. Pravidlo 4%: z portfolia můžete ročně vybírat 4% a statisticky vydrží navždy."
      result={result}
      showResult={true}
      testId="retirement-calculator"
    >
      <div className="grid grid-cols-2 gap-4">
        <SliderInput
          label="Váš věk"
          value={currentAge}
          onChange={setCurrentAge}
          min={18}
          max={60}
          step={1}
          suffix="let"
          testId="retirement-age-slider"
        />
        <SliderInput
          label="Odchod do důchodu"
          value={retirementAge}
          onChange={setRetirementAge}
          min={55}
          max={75}
          step={1}
          suffix="let"
          testId="retirement-retire-slider"
        />
      </div>
      <SliderInput
        label="Současné úspory"
        value={currentSavings}
        onChange={setCurrentSavings}
        min={0}
        max={5000000}
        step={50000}
        suffix="Kč"
        testId="retirement-savings-slider"
      />
      <SliderInput
        label="Měsíční spoření"
        value={monthlyContribution}
        onChange={setMonthlyContribution}
        min={500}
        max={30000}
        step={500}
        suffix="Kč"
        testId="retirement-contribution-slider"
      />
      <SliderInput
        label="Kolik budete v důchodu potřebovat"
        value={monthlyRetirementNeeds}
        onChange={setMonthlyRetirementNeeds}
        min={15000}
        max={100000}
        step={1000}
        suffix="Kč"
        testId="retirement-needs-slider"
      />
    </CalculatorCard>
  );
};
